import React, { useState, useEffect } from 'react';
import { Briefcase, CheckCircle, ChevronRight, Loader, Info } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { createJob, updateJob, fetchJob, fetchJobOptions } from '../services/api';
import LocationInput from '../components/LocationInput';
import WorkdaysPicker from '../components/WorkdaysPicker';
import {
  JOB_TYPE_LABELS, BRANCHE_LABELS, DAYTIME_LABELS, JOB_SKILL_GROUPS,
  labelOf, weekdaysLabel, hasGenderTag, jobTitle, VOLLZEIT_HINT,
} from '../constants/jobOptions';
import './PostJobPage.css';

const STEPS = ['Basis-Info', 'Details', 'Vorschau'];

// Fallback, falls GET /api/jobs/options nicht lädt
const FALLBACK_OPTIONS = {
  job_types: Object.keys(JOB_TYPE_LABELS),
  branchen: Object.keys(BRANCHE_LABELS),
  daytimes: Object.keys(DAYTIME_LABELS),
};

const EMPTY_FORM = {
  title: '',
  location: '',
  locationId: null,
  jobType: 'aushilfe',
  branche: '',
  description: '',
  requirements: '',
  tags: '',
  skills: [],
  weekdays: [],
  daytime: [],
  daysPerWeek: '',
  hoursMin: '',
  hoursMax: '',
  payPerHour: '',
  payNote: '',
  minAge: '',
  startMode: 'asap', // 'asap' | 'date'
  startDate: '',
  durationWeeks: '',
  noExperienceRequired: true,
  driversLicenseRequired: false,
  remotePossible: false,
};

// Für die Anzeige auf Karten/Listen: das Backend leitet salary nicht ab
const salaryText = (payPerHour, payNote) => {
  const parts = [];
  if (payPerHour) parts.push(`${String(payPerHour).replace('.', ',')} €/Std.`);
  if (payNote) parts.push(payNote);
  return parts.join(' · ');
};

export default function PostJobPage() {
  const { user, openAuth } = useApp();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loadingJob, setLoadingJob] = useState(isEdit);
  const [options, setOptions] = useState(FALLBACK_OPTIONS);
  const [form, setForm] = useState(EMPTY_FORM);

  // Auswahllisten kommen als Slugs vom Backend
  useEffect(() => {
    fetchJobOptions()
      .then(({ data }) => setOptions(o => ({ ...o, ...data })))
      .catch(() => {});
  }, []);

  // Bearbeiten: bestehendes Inserat laden
  useEffect(() => {
    if (!isEdit) return;
    setLoadingJob(true);
    fetchJob(id)
      .then(({ data }) => {
        const j = data.job || data;
        setForm({
          ...EMPTY_FORM,
          title: j.title || '',
          location: j.location || '',
          locationId: j.location_id ?? null,
          jobType: j.job_type || 'aushilfe',
          branche: j.branche || '',
          description: j.description || '',
          requirements: j.requirements || '',
          tags: (j.tags || []).join(', '),
          skills: j.skills || [],
          weekdays: j.weekdays || [],
          daytime: j.daytime || [],
          daysPerWeek: j.days_per_week ?? '',
          hoursMin: j.hours_per_week_min ?? '',
          hoursMax: j.hours_per_week_max ?? '',
          payPerHour: j.pay_per_hour ?? '',
          payNote: j.pay_note || '',
          minAge: j.min_age ?? '',
          startMode: j.start_date && !j.start_asap ? 'date' : 'asap',
          startDate: j.start_date ? String(j.start_date).slice(0, 10) : '',
          durationWeeks: j.duration_weeks ?? '',
          noExperienceRequired: j.no_experience_required ?? true,
          driversLicenseRequired: j.drivers_license_required ?? false,
          remotePossible: j.remote_possible ?? false,
        });
      })
      .catch(() => setSubmitError('Inserat konnte nicht geladen werden.'))
      .finally(() => setLoadingJob(false));
  }, [id, isEdit]);

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const toggleArr = (field, value) => {
    setForm(f => {
      const arr = f[field];
      return { ...f, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  if (!user) {
    return (
      <div className="post-job-gate">
        <Briefcase size={48} />
        <h2>Für Arbeitgeber</h2>
        <p>Erstelle ein Konto als Arbeitgeber, um Inserate aufzugeben.</p>
        <button className="btn-primary-solid" onClick={() => openAuth('register')}>
          Als Arbeitgeber registrieren
        </button>
      </div>
    );
  }

  if (loadingJob) {
    return (
      <div className="post-job-gate">
        <Loader size={32} className="spin" />
        <p>Inserat wird geladen...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="post-job-success">
        <div className="post-job-success__icon">
          <CheckCircle size={48} />
        </div>
        <h2>{isEdit ? 'Inserat aktualisiert!' : 'Inserat erfolgreich aufgegeben!'}</h2>
        <p>
          {isEdit
            ? 'Deine Änderungen sind gespeichert und sofort sichtbar.'
            : 'Dein Job-Inserat wurde veröffentlicht und ist jetzt für Bewerber sichtbar.'}
        </p>
        <div className="post-job-success__actions">
          <button className="btn-primary-solid" onClick={() => navigate('/')}>Meine Inserate</button>
          {!isEdit && (
            <button className="btn-outline" onClick={() => {
              setSubmitted(false);
              setStep(0);
              setForm(EMPTY_FORM);
            }}>
              Weiteres Inserat
            </button>
          )}
        </div>
      </div>
    );
  }

  // Pflichtfelder je Schritt — sonst läuft man erst beim Absenden in den 400er
  const stepError = () => {
    if (step === 0) {
      if (!form.title.trim()) return 'Bitte gib eine Stellenbezeichnung an.';
      // Nur Orte aus der Vorschlagsliste, Freitext hat keine location_id
      if (!form.locationId) return 'Bitte wähle einen Ort aus der Vorschlagsliste.';
      if (!form.branche) return 'Bitte wähle eine Branche.';
    }
    if (step === 1 && !form.description.trim()) return 'Bitte beschreibe die Stelle.';
    return null;
  };

  const handleNext = async (e) => {
    e.preventDefault();
    const err = stepError();
    if (err) { setSubmitError(err); return; }
    setSubmitError(null);

    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
      return;
    }

    setSubmitting(true);
    try {
      // Leerstrings sind für das Backend "nicht angegeben"
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        requirements: form.requirements.trim(),
        location_id: form.locationId,
        job_type: form.jobType,
        branche: form.branche,
        salary: salaryText(form.payPerHour, form.payNote),
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        skills: form.skills,
        weekdays: form.weekdays,
        daytime: form.daytime,
        days_per_week: form.daysPerWeek,
        hours_per_week_min: form.hoursMin,
        hours_per_week_max: form.hoursMax,
        pay_per_hour: form.payPerHour,
        pay_note: form.payNote.trim(),
        min_age: form.minAge,
        start_asap: form.startMode === 'asap',
        start_date: form.startMode === 'date' ? form.startDate : '',
        duration_weeks: form.durationWeeks,
        no_experience_required: form.noExperienceRequired,
        drivers_license_required: form.driversLicenseRequired,
        remote_possible: form.remotePossible,
      };
      if (isEdit) await updateJob(id, payload);
      else await createJob(payload);
      setSubmitted(true);
    } catch (err) {
      const res = err.response?.data;
      setSubmitError(
        res?.errors?.[0]?.msg || res?.error || res?.message ||
        'Inserat konnte nicht gespeichert werden. Bitte versuche es erneut.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const companyName = user.company_name || 'Dein Unternehmen';

  return (
    <div className="post-job-page">
      <div className="container">
        <div className="post-job-header">
          <h1>{isEdit ? 'Inserat bearbeiten' : 'Inserat aufgeben'}</h1>
          <p>Erreiche motivierte Jugendliche in der Pfalz.</p>
        </div>

        {/* Progress */}
        <div className="post-job-steps">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`post-job-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                <div className="post-job-step__circle">
                  {i < step ? '✓' : i + 1}
                </div>
                <span>{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`post-job-step__line ${i < step ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <form className="post-job-form" onSubmit={handleNext}>
          {step === 0 && (
            <div className="post-job-fields animate-fadeInUp">
              <h2>Basis-Informationen</h2>

              <div className="form-group">
                <label htmlFor="post-job-title">Stellenbezeichnung *</label>
                {/* "(m/w/d)" ist fest und wird nicht gespeichert, die Anzeige hängt es an */}
                <div className="post-job-title">
                  <input
                    id="post-job-title"
                    placeholder="z.B. Aushilfe Kasse"
                    value={form.title}
                    onChange={e => update('title', e.target.value)}
                  />
                  {!hasGenderTag(form.title) && <span className="post-job-title__suffix">(m/w/d)</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ort *</label>
                  <LocationInput
                    value={form.location}
                    onChange={(text, loc) =>
                      setForm(f => ({ ...f, location: text, locationId: loc?.id ?? null }))
                    }
                    placeholder="Ort suchen, z.B. Kaiserslautern"
                  />
                </div>
                <div className="form-group">
                  <label>Jobtyp *</label>
                  <select value={form.jobType} onChange={e => update('jobType', e.target.value)}>
                    {options.job_types.map(slug => (
                      <option key={slug} value={slug}>{labelOf(JOB_TYPE_LABELS, slug)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {form.jobType === 'vollzeit' && (
                <p className="post-job-hint"><Info size={14} /> {VOLLZEIT_HINT}</p>
              )}

              <div className="form-group">
                <label>Branche *</label>
                <select value={form.branche} onChange={e => update('branche', e.target.value)}>
                  <option value="">Bitte wählen...</option>
                  {options.branchen.map(slug => (
                    <option key={slug} value={slug}>{labelOf(BRANCHE_LABELS, slug)}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Stundenlohn (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="z.B. 13.50"
                    value={form.payPerHour}
                    onChange={e => update('payPerHour', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Hinweis zur Vergütung</label>
                  <input
                    maxLength={200}
                    placeholder="z.B. plus Trinkgeld"
                    value={form.payNote}
                    onChange={e => update('payNote', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="post-job-fields animate-fadeInUp">
              <h2>Details & Anforderungen</h2>

              <div className="form-group">
                <label>Arbeitstage</label>
                <WorkdaysPicker
                  value={form.weekdays}
                  onChange={weekdays => update('weekdays', weekdays)}
                  chipClass="form-chip"
                  activeClass="active"
                />
              </div>

              <div className="form-group">
                <label>Tageszeit</label>
                <div className="form-chips">
                  {options.daytimes.map(t => (
                    <button
                      key={t}
                      type="button"
                      className={`form-chip${form.daytime.includes(t) ? ' active' : ''}`}
                      onClick={() => toggleArr('daytime', t)}
                    >
                      {labelOf(DAYTIME_LABELS, t)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tage pro Woche</label>
                  <input
                    type="number" min="1" max="7" placeholder="z.B. 3"
                    value={form.daysPerWeek}
                    onChange={e => update('daysPerWeek', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Mindestalter</label>
                  <input
                    type="number" min="13" max="67" placeholder="z.B. 16"
                    value={form.minAge}
                    onChange={e => update('minAge', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Stunden/Woche von</label>
                  <input
                    type="number" min="1" max="60" placeholder="z.B. 8"
                    value={form.hoursMin}
                    onChange={e => update('hoursMin', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Stunden/Woche bis</label>
                  <input
                    type="number" min="1" max="60" placeholder="z.B. 15"
                    value={form.hoursMax}
                    onChange={e => update('hoursMax', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Start</label>
                <div className="form-chips">
                  <button
                    type="button"
                    className={`form-chip${form.startMode === 'asap' ? ' active' : ''}`}
                    onClick={() => update('startMode', 'asap')}
                  >
                    Ab sofort
                  </button>
                  <button
                    type="button"
                    className={`form-chip${form.startMode === 'date' ? ' active' : ''}`}
                    onClick={() => update('startMode', 'date')}
                  >
                    Ab Datum
                  </button>
                </div>
              </div>

              {form.startMode === 'date' && (
                <div className="form-group">
                  <label>Startdatum</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => update('startDate', e.target.value)}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Dauer (Wochen)</label>
                <input
                  type="number" min="1" max="520" placeholder="leer = unbefristet"
                  value={form.durationWeeks}
                  onChange={e => update('durationWeeks', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Ohne Vorerfahrung möglich?</label>
                <div className="form-chips">
                  <button type="button"
                    className={`form-chip${form.noExperienceRequired ? ' active' : ''}`}
                    onClick={() => update('noExperienceRequired', true)}>Ja</button>
                  <button type="button"
                    className={`form-chip${!form.noExperienceRequired ? ' active' : ''}`}
                    onClick={() => update('noExperienceRequired', false)}>Nein</button>
                </div>
              </div>

              <div className="form-group">
                <label>Führerschein erforderlich?</label>
                <div className="form-chips">
                  <button type="button"
                    className={`form-chip${form.driversLicenseRequired ? ' active' : ''}`}
                    onClick={() => update('driversLicenseRequired', true)}>Ja</button>
                  <button type="button"
                    className={`form-chip${!form.driversLicenseRequired ? ' active' : ''}`}
                    onClick={() => update('driversLicenseRequired', false)}>Nein</button>
                </div>
              </div>

              <div className="form-group">
                <label>Remote möglich?</label>
                <div className="form-chips">
                  <button type="button"
                    className={`form-chip${form.remotePossible ? ' active' : ''}`}
                    onClick={() => update('remotePossible', true)}>Ja</button>
                  <button type="button"
                    className={`form-chip${!form.remotePossible ? ' active' : ''}`}
                    onClick={() => update('remotePossible', false)}>Nein</button>
                </div>
              </div>

              <div className="form-group">
                <label>Stellenbeschreibung *</label>
                <textarea
                  rows={4}
                  placeholder="Beschreibe die Stelle und was du bietest..."
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Anforderungen</label>
                <textarea
                  rows={3}
                  placeholder="Was sollten Bewerber mitbringen? (optional)"
                  value={form.requirements}
                  onChange={e => update('requirements', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Skills (optional)</label>
                <p className="post-job-skills__hint">Nur zur Beschreibung, Bewerber werden danach nicht gefiltert.</p>
                {JOB_SKILL_GROUPS.map(group => (
                  <div key={group.title} className="post-job-skills__group">
                    <span className="post-job-skills__title">{group.title}</span>
                    <div className="form-chips">
                      {group.skills.map(skill => (
                        <button
                          key={skill}
                          type="button"
                          className={`form-chip${form.skills.includes(skill) ? ' active' : ''}`}
                          aria-pressed={form.skills.includes(skill)}
                          onClick={() => toggleArr('skills', skill)}
                        >
                          {skill}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label>Tags (kommagetrennt)</label>
                <input
                  placeholder="Teamarbeit, Flexibel, Schüler willkommen"
                  value={form.tags}
                  onChange={e => update('tags', e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="post-job-preview animate-fadeInUp">
              <h2>Vorschau</h2>
              <div className="post-job-preview__card">
                <div className="post-job-preview__header">
                  <div className="post-job-preview__logo">{companyName.charAt(0)}</div>
                  <div>
                    <p className="post-job-preview__company">{companyName}</p>
                    <h3 className="post-job-preview__title">{jobTitle(form.title) || 'Stellenbezeichnung'}</h3>
                  </div>
                </div>
                <div className="post-job-preview__meta">
                  <span>📍 {form.location || 'Ort'}</span>
                  <span>⏱ {labelOf(JOB_TYPE_LABELS, form.jobType)}</span>
                  <span>🏷 {labelOf(BRANCHE_LABELS, form.branche) || 'Branche'}</span>
                  <span>💰 {salaryText(form.payPerHour, form.payNote) || 'Nicht angegeben'}</span>
                </div>
                <div className="post-job-preview__meta">
                  <span>📅 {weekdaysLabel(form.weekdays)}</span>
                  {form.daytime.length > 0 && (
                    <span>🕐 {form.daytime.map(d => labelOf(DAYTIME_LABELS, d)).join(', ')}</span>
                  )}
                </div>
                <div className="post-job-preview__meta">
                  <span>🚀 {form.startMode === 'asap' ? 'Ab sofort' : (form.startDate || 'Startdatum offen')}</span>
                  {form.minAge && <span>🔞 ab {form.minAge} Jahren</span>}
                  {form.remotePossible && <span>💻 Remote möglich</span>}
                </div>
                <p className="post-job-preview__desc">{form.description || 'Beschreibung...'}</p>
              </div>
              <div className="post-job-preview__note">
                ✅ Dein Inserat ist nach dem Speichern sofort sichtbar.
              </div>
            </div>
          )}

          {submitError && <div className="post-job-error">{submitError}</div>}

          <div className="post-job-nav">
            {step > 0 && (
              <button type="button" className="btn-outline" onClick={() => { setSubmitError(null); setStep(s => s - 1); }} disabled={submitting}>
                Zurück
              </button>
            )}
            <button type="submit" className="btn-primary-solid post-job-nav__next" disabled={submitting}>
              {submitting ? (
                <><Loader size={16} className="spin" /><span>Wird gespeichert...</span></>
              ) : step < STEPS.length - 1 ? (
                <><span>Weiter</span><ChevronRight size={18} /></>
              ) : (
                <><span>{isEdit ? 'Änderungen speichern' : 'Inserat veröffentlichen'}</span><CheckCircle size={18} /></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
